import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import themeGet from '@styled-system/theme-get';

import { Context } from '../../Providers/Blade';
import MenuItem from '../MenuItem';

import { Login, Menu, MenuButton, Play } from './styles';

const infoBarBreakpoint = 600;

export const InfoBar = styled(({ className, textHtml, ctaText, ctaUrl }) => (
  <div className={className}>
    <div className="infobar-wrapper">
      {ReactHtmlParser(textHtml)}
      <a href={ctaUrl}>
        <div className="infobar-cta">
          <h3>{ctaText}</h3>
        </div>
      </a>
    </div>
  </div>
))`
  width: 100%;
  min-height: 50px;
  background-color: ${({ background }) =>
    background || themeGet('colors.white')};
  color: ${({ foreground }) => foreground || themeGet('colors.black')};
  display: flex;
  align-items: center;

  &.hide {
    display: none;
  }

  .infobar-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: ${infoBarBreakpoint}px) {
      flex-direction: column;
    }
  }

  h3 {
    font-size: 16px;
    font-weight: 500;
    color: inherit;
  }

  & .infobar-cta {
    width: 200px;
    height: 34px;
    border-radius: 2px;
    margin: 0 0 0 25px;
    background-color: ${themeGet('colors.white')};
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${themeGet('colors.orange')};

    @media (max-width: ${infoBarBreakpoint}px) {
      margin: 8px 0;
    }
  }
`;

export const MobileNav = styled(({ className, loginLink, loginText, location }) => {
  const { setState } = useContext(Context);

  // We force the blade menu to be closed when we go from mobile -> desktop
  let mediaQuery;
  const matchMedia = (breakpoint = mediaQuery) => {
    if (!breakpoint.matches) {
      setState(false);
    }
  };
  useEffect(() => {
    if (typeof window !== 'undefined') {
      mediaQuery = window.matchMedia(
        `(max-width: ${themeGet('breakpoints.ml')})`
      );
      mediaQuery.addListener(matchMedia);
      matchMedia();
    }
    return () => {
      mediaQuery.removeListener(matchMedia);
    };
  }, []);

  return (
    <div className={className}>
      {!(location && location.pathname.includes('/tutoring')) &&
      <Login
        to={loginLink.url}
        name="login"
        ariaLabel={loginLink.ariaLabelLogin}
        queryString={loginLink.queryStringLogin}
      >
        {loginText}
      </Login>
      }
      <MenuButton
        onClick={() => setState(true)}
        name="menu"
        aria-label="open the menu"
      />
    </div>
  );
})`
  && {
    @media (min-width: 961px) {
      display: none;
    }
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
`;

MobileNav.propTypes = {
  loginLink: PropTypes.string.isRequired,
  loginText: PropTypes.string.isRequired,
};

export const DesktopNav = styled(
  ({
    className,
    menu,
    openSections,
    location,
    onClick,
    loginLink,
    loginText,
    playLink,
    playText,
    resetState,
  }) => (
    <div className={className}>
      <Menu className="no-bullets">
        {menu &&
          menu.map((info, index) => {
            return (
              <li key={`mobile-menu-item-${index.toString()}`}>
                <MenuItem
                  data={info}
                  isOpen={!!openSections[info.text]}
                  label={info.text}
                  ariaLabel={info.text}
                  onClick={onClick}
                  resetState={resetState}
                />
              </li>
            );
          })}
      </Menu>
      {!(location && location.pathname.includes('/tutoring')) &&
        <Login
          to={loginLink.url}
          name="login"
          ariaLabel={loginLink.ariaLabelLogin}
          queryString={loginLink.queryStringLogin}
        >
          {loginText}
        </Login> 
      }
      { location && location.pathname.includes('/tutoring') ?
        <Play
            to="https://www.prodigygame.com/tutoring/public/identification/"
            name="play"
            ariaLabel={playLink.ariaLabelPlay}
            queryString={playLink.queryStringPlay}
            className="solid-on-light-bg"
          >
            Book free session
        </Play> :
        <Play
          to={playLink.url}
          name="play"
          ariaLabel={playLink.ariaLabelPlay}
          queryString={playLink.queryStringPlay}
          className="solid-on-light-bg"
          >
          {playText}
        </Play>
      }
    </div>
  )
)`
  && {
    width: 100%;
    display: flex;
    @media (max-width: ${themeGet('breakpoints.ml')}) {
      display: none;
    }
  }
  & li > div {
    margin: 0 10px;
    }
  }
  & li > div.slideout {
    margin: 0;
    }
  }
`;

DesktopNav.propTypes = {
  menu: PropTypes.arrayOf(PropTypes.shape()),
  openSections: PropTypes.shape(),
  onClick: PropTypes.func,
  loginLink: PropTypes.string,
  loginText: PropTypes.string,
  playLink: PropTypes.string,
  playText: PropTypes.string,
};